import { useCallback, useEffect, useState, lazy, Suspense } from "react";
import { Zap, Euro, Clock, Leaf, RotateCcw, Package } from "lucide-react";
import ResultCard from "../shared/ResultCard";
import PDFDownloadGate from "../shared/PDFDownloadGate";
import AnsprechpartnerCard from "../shared/AnsprechpartnerCard";
import LoadingSpinner from "../shared/LoadingSpinner";
import ErrorState from "../shared/ErrorState";
import SoGehtEsWeiter from "../shared/SoGehtEsWeiter";
import { ROIFormData } from "./ROISimulator";
import { api } from "../../lib/api";
import { formatEuro, formatKWh, formatKWp, formatProzent } from "../../lib/format";
import { RATES_VERSION } from "../../lib/rates";

// Recharts lazy-laden für Bundle-Optimierung
const AmortisationChart = lazy(() => import("./AmortisationChart"));

interface JahresCashflow {
  jahr: number;
  ertrag_kwh: number;
  eigenverbrauch_kwh: number;
  einspeisung_kwh: number;
  ersparnis_euro: number;
  einspeisung_euro: number;
  wartung_euro: number;
  cashflow_euro: number;
  kumuliert_euro: number;
}

interface ROIResult {
  anlage: { kwp: number; jahresertrag_kwh: number; module_anzahl: number; speicher_kwh?: number; paket?: "basic" | "premium" };
  wirtschaftlichkeit: {
    investitionskosten: number;
    amortisation_jahre: number;
    gesamtersparnis_25j: number;
    rendite_prozent: number;
    eigenverbrauchsquote: number;
    autarkiegrad: number;
  };
  jahres_cashflow: JahresCashflow[];
  umwelt: { co2_einsparung_kg_jahr: number; co2_einsparung_25j_tonnen: number; baeume_equivalent: number };
  disclaimer: string;
  berechnet_am: string;
}

const ECOFLOW = {
  modul: "Trina Solar Vertex S+ 450 Wp All-Black",
  wechselrichter: "EcoFlow Wechselrichter",
  speicher: "EcoFlow PowerOcean",
} as const;

interface Props {
  data: ROIFormData;
  onBack: () => void;
  onReset: () => void;
}


export default function Step4Ergebnis({ data, onBack, onReset }: Props) {
  const [result, setResult] = useState<ROIResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const calc = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.roiSimulator.calculate({ ...data, paket: "premium" });
      setResult(res as ROIResult);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Berechnung fehlgeschlagen. Bitte prüfen Sie Ihre Verbindung.");
    } finally {
      setLoading(false);
    }
  }, [data]);

  useEffect(() => { calc(); }, []);

  if (loading) return <LoadingSpinner text="Rendite wird berechnet…" />;

  if (error) return <ErrorState message={error} onRetry={calc} onBack={onBack} />;

  if (!result) return null;

  const w = result.wirtschaftlichkeit;

  return (
    <div className="animate-slide-up" data-testid="step-4-ergebnis">
      {/* Highlight-Karten */}
      <div className="grid grid-cols-2 gap-3 mb-6" aria-live="polite" aria-label="Berechnungsergebnis">
        <ResultCard
          label="Ersparnis (25 Jahre)"
          value={formatEuro(w.gesamtersparnis_25j)}
          subValue={`${w.rendite_prozent} % Gesamtrendite`}
          green
          icon={<Euro className="w-4 h-4" />}
          tooltip="Gesamte Ersparnis über 25 Jahre durch Eigenverbrauch und Einspeisung, abzüglich Wartungskosten und Investition."
          testId="gesamtersparnis-card"
        />
        <ResultCard
          label="Amortisation"
          value={`${w.amortisation_jahre} Jahre`}
          subValue="bis zur Kostendeckung"
          icon={<Clock className="w-4 h-4" />}
          tooltip="Nach dieser Zeit hat sich Ihre PV-Anlage durch Stromersparnis und Einspeisevergütung vollständig bezahlt gemacht."
          testId="amortisation-card"
        />
        <ResultCard
          label="Jahresertrag"
          value={formatKWh(result.anlage.jahresertrag_kwh)}
          subValue={`${formatKWp(result.anlage.kwp)} · ${result.anlage.module_anzahl} Module`}
          icon={<Zap className="w-4 h-4" />}
          tooltip="Die Strommenge, die Ihre Anlage im ersten Jahr voraussichtlich produziert. Basis: Standort Rottweil, Ihre Dachausrichtung und Neigung."
          testId="jahresertrag-card"
        />
        <ResultCard
          label="CO₂-Einsparung"
          value={`${result.umwelt.co2_einsparung_kg_jahr.toLocaleString("de-DE")} kg/Jahr`}
          subValue={`≈ ${result.umwelt.baeume_equivalent} Bäume`}
          icon={<Leaf className="w-4 h-4" />}
          tooltip="Jährliche CO₂-Einsparung im Vergleich zum deutschen Strommix. Der Bäume-Vergleich zeigt, wie viele Bäume die gleiche Menge CO₂ binden würden."
          testId="co2-card"
        />
      </div>

      {/* Ausstattung / Richtpreis — EcoFlow */}
      <div className="bg-neko-dark-2 border border-neko-dark-3 rounded-xl p-4 mb-4">
        <h3 className="text-sm font-semibold text-neko-white mb-3 flex items-center gap-2">
          <Package className="w-4 h-4 text-neko-orange" />
          Ihre Ausstattung — Richtpreis
        </h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-neko-gray">Solarmodule</span>
            <span className="text-neko-white font-medium">{result.anlage.module_anzahl}× {ECOFLOW.modul}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-neko-gray">Wechselrichter</span>
            <span className="text-neko-white font-medium">{ECOFLOW.wechselrichter}</span>
          </div>
          {data.mit_speicher && result.anlage.speicher_kwh && (
            <div className="flex justify-between">
              <span className="text-neko-gray">Batteriespeicher</span>
              <span className="text-neko-white font-medium">{ECOFLOW.speicher} · {result.anlage.speicher_kwh} kWh</span>
            </div>
          )}
          <div className="border-t border-neko-dark-3 pt-2 flex justify-between font-bold">
            <span className="text-neko-white">Richtpreis (inkl. Montage)</span>
            <span className="text-neko-orange">{formatEuro(w.investitionskosten)}</span>
          </div>
        </div>
        <p className="text-[10px] text-neko-gray mt-2">
          Unverbindlicher Richtpreis inkl. Installation. Ihr individuelles Angebot erhalten Sie nach der Beratung.
        </p>
      </div>

      {/* Weitere Kennzahlen */}
      <div className="bg-neko-dark-2 border border-neko-dark-3 rounded-xl p-4 mb-4">
        <h3 className="text-sm font-semibold text-neko-white mb-3">Anlage &amp; Wirtschaftlichkeit</h3>
        <div className="space-y-2 text-sm">
          {(
            [
              ["Investitionskosten", formatEuro(w.investitionskosten)],
              ["Eigenverbrauchsquote", formatProzent(w.eigenverbrauchsquote)],
              ["Autarkiegrad", formatProzent(w.autarkiegrad)],
              ["CO₂-Einsparung (25J)", `${result.umwelt.co2_einsparung_25j_tonnen} Tonnen`],
              ...(data.mit_speicher && result.anlage.speicher_kwh
                ? [["Batteriespeicher", `${ECOFLOW.speicher} · ${result.anlage.speicher_kwh} kWh`]]
                : []),
            ] as [string, string][]
          ).map(([key, val]) => (
              <div key={key as string} className="flex justify-between">
                <span className="text-neko-gray">{key as string}</span>
                <span className="text-neko-white font-medium">{val as string}</span>
              </div>
            ))}
        </div>
      </div>

      {/* Amortisations-Chart */}
      <div className="bg-neko-dark-2 border border-neko-dark-3 rounded-xl p-4 mb-4">
        <h3 className="text-sm font-semibold text-neko-white mb-3">25-Jahres-Entwicklung</h3>
        <Suspense fallback={<LoadingSpinner text="Diagramm wird geladen…" />}>
          <AmortisationChart
            data={result.jahres_cashflow}
            amortisationJahr={w.amortisation_jahre}
          />
        </Suspense>
      </div>

      {/* So geht es weiter */}
      <SoGehtEsWeiter testId="so-geht-es-weiter" />

      {/* Ansprechpartner */}
      <AnsprechpartnerCard
        name="Fabian Bahr"
        title="Geschäftsführer"
        subtitle="Ihr Ansprechpartner für PV-Anlagen"
        imageSrc="/images/fabian-bahr.jpg"
        phone="+49742163340"
        phoneDisplay="07421 6334-0"
        email="info@neko-bws.de"
        whatsapp="491738101485"
        testId="ansprechpartner-card"
      />

      {/* PDF Download */}
      <div className="mb-4">
        <PDFDownloadGate
          tool="roi-simulator"
          eingabedaten={data as unknown as Record<string, unknown>}
          ergebnis={result as unknown as Record<string, unknown>}
        />
      </div>

      <p className="text-xs text-neko-gray text-center mb-4">{result.disclaimer}</p>

      <p className="text-xs text-neko-gray mt-2 text-center" data-testid="rates-version">
        v{RATES_VERSION}
      </p>

      <button
        onClick={onReset}
        className="w-full flex items-center justify-center gap-2 py-3 mt-4 rounded-xl bg-tenant-primary text-white font-semibold text-sm transition-all hover:opacity-90"
        data-testid="reset-btn"
      >
        <RotateCcw className="w-4 h-4" /> Neue Berechnung starten
      </button>
    </div>
  );
}
